// Guest Memory — returning guests' history, preferences & AI welcome hint for tomorrow's arrivals.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Brain, Loader2, RefreshCw, Sparkles, Star, UtensilsCrossed, Save, Printer, Wand2, Mail, Download } from 'lucide-react';
import { apiCall, API_URL, getToken } from './_shared';
import GuestProfileModal, { GuestSearchBox } from './GuestProfileModal';

const iso = (d) => d.toISOString().slice(0, 10);
export const downloadPdf = async (path, filename) => {
  const token = getToken();
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) { alert(`Download failed (${res.status})`); return; }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
};
const TAG_CLS = {
  vip: 'bg-amber-100 text-amber-800 border-amber-300', quiet: 'bg-sky-100 text-sky-800 border-sky-300',
  allergic: 'bg-red-100 text-red-800 border-red-300', late: 'bg-purple-100 text-purple-800 border-purple-300',
  pillow: 'bg-indigo-100 text-indigo-800 border-indigo-300', note: 'bg-slate-100 text-slate-800 border-slate-300',
};
const EDIT_ROLES = ['super_admin', 'admin', 'manager', 'reception'];

export const GuestRow = ({ g, date, canEdit, onSaved, hintBody, profile = false }) => {
  const [memo, setMemo] = useState(g.preferences || '');
  const [saving, setSaving] = useState(false);
  const [hinting, setHinting] = useState(false);
  const [hint, setHint] = useState(g.ai_hint || '');
  const [err, setErr] = useState('');
  useEffect(() => { setMemo(g.preferences || ''); setHint(g.ai_hint || ''); }, [g.preferences, g.ai_hint]);
  const dirty = memo !== (g.preferences || '');
  const masked = (g.guest_name || '').trim().toLowerCase() === 'guest';

  const saveMemo = async () => {
    setSaving(true); setErr('');
    try { await apiCall('/api/admin/reception/guest-memory/notes', { method: 'PUT', body: JSON.stringify({ name: g.guest_name, preferences: memo }) }); onSaved?.(); }
    catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  const genHint = async () => {
    setHinting(true); setErr('');
    try { const r = await apiCall('/api/admin/reception/guest-memory/ai-hint', { method: 'POST', body: JSON.stringify(hintBody || { name: g.guest_name, date_str: date }) }); setHint(r.ai_hint); }
    catch (e) { setErr(e.message); } finally { setHinting(false); }
  };

  const tagChips = (g.tags || []).map((t, i) => (
    <span key={`${t.label}-${i}`} className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${TAG_CLS[t.kind] || TAG_CLS.note}`}>{t.label}</span>
  ));
  const orderChips = (g.favourite_orders || []).map((o) => (
    <span key={o.name} className="inline-flex items-center gap-1 text-[10px] bg-orange-50 text-orange-800 border border-orange-200 rounded px-1.5 py-0.5"><UtensilsCrossed className="w-3 h-3" />{o.name} ×{o.qty}</span>
  ));
  const noteItems = (g.past_notes || []).map((n, i) => <li key={i} className="text-[11px] text-slate-600 italic truncate">“{n}”</li>);

  return (
    <li className={`rounded-lg border p-3 ${g.returning ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`} data-testid={`guest-memory-row-${g.booking_id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-slate-900 truncate" data-testid={`guest-memory-name-${g.booking_id}`}>{g.guest_name}</p>
            {g.returning ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-white px-2 py-0.5 rounded-full" data-testid={`guest-memory-badge-${g.booking_id}`}>
                <Star className="w-3 h-3" /> Returning · visit #{g.visit_number}
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full" data-testid={`guest-memory-badge-${g.booking_id}`}>First visit</span>
            )}
            {tagChips}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            {!profile && <>{g.room_name || 'Room TBD'} · {g.adults} ad{g.children ? ` + ${g.children} ch` : ''}</>}
            {profile && <>{g.stays} past stay{g.stays === 1 ? '' : 's'} · {g.total_nights} nights{g.email ? ` · ${g.email}` : ''}{g.phone ? ` · ${g.phone}` : ''}</>}
            {g.returning && g.last_stay && <> · Last stay {g.last_stay.check_in} ({g.last_stay.room_name || '—'})</>}
            {g.returning && g.favourite_room && <> · Loves <b>{g.favourite_room}</b></>}
            {g.returning && g.total_spend > 0 && <> · €{g.total_spend.toFixed(0)} lifetime</>}
          </p>
        </div>
        {g.returning && canEdit && !masked && !profile && (
          <button type="button" onClick={genHint} disabled={hinting} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2.5 py-1 hover:bg-violet-100 disabled:opacity-60" data-testid={`guest-memory-hint-btn-${g.booking_id}`}>
            {hinting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {hint ? 'Regenerate hint' : 'AI welcome hint'}
          </button>
        )}
      </div>
      {hint && (
        <p className="mt-2 text-sm text-violet-900 bg-violet-50 border-l-4 border-violet-400 rounded px-3 py-1.5" data-testid={`guest-memory-hint-${g.booking_id}`}>
          <Sparkles className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-violet-500" />{hint}
        </p>
      )}
      {(orderChips.length > 0 || noteItems.length > 0) && (
        <div className="mt-2 space-y-1">
          {orderChips.length > 0 && <div className="flex flex-wrap gap-1">{orderChips}</div>}
          {noteItems.length > 0 && <ul className="space-y-0.5">{noteItems}</ul>}
        </div>
      )}
      {g.booking_notes && <p className="mt-1 text-[11px] text-slate-700"><span className="font-semibold">Booking note:</span> {g.booking_notes}</p>}
      {canEdit && !masked && (
        <div className="mt-2 flex items-start gap-2">
          <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={1} maxLength={600} placeholder="Preferences memo — pillows, milk, floor, room number…"
            className="flex-1 text-xs border rounded px-2 py-1 resize-y min-h-[28px]" data-testid={`guest-memory-memo-${g.booking_id}`} />
          {dirty && (
            <button type="button" onClick={saveMemo} disabled={saving} className="inline-flex items-center gap-1 text-xs font-bold bg-orange-500 text-white rounded px-2 py-1 hover:bg-orange-600 disabled:opacity-60" data-testid={`guest-memory-memo-save-${g.booking_id}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
          )}
        </div>
      )}
      {!canEdit && g.preferences && <p className="mt-1 text-xs text-slate-700"><span className="font-semibold">Preferences:</span> {g.preferences}</p>}
      {masked && canEdit && <p className="mt-1 text-[10px] text-amber-700">Set the real name in Reception to unlock memory for this guest.</p>}
      {err && <p className="mt-1 text-xs text-red-600" data-testid={`guest-memory-error-${g.booking_id}`}>{err}</p>}
    </li>
  );
};

const GuestMemoryCard = ({ userRole, compact = false }) => {
  const [day, setDay] = useState('tomorrow');
  const [custom, setCustom] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(!compact);
  const [batching, setBatching] = useState(false);
  const [batchMsg, setBatchMsg] = useState('');
  const [mailOpen, setMailOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [email, setEmail] = useState('');
  const [mailMsg, setMailMsg] = useState('');
  const [mailBusy, setMailBusy] = useState(false);
  const [profileName, setProfileName] = useState('');
  const canEdit = EDIT_ROLES.includes(userRole);
  const canManage = ['super_admin', 'admin', 'manager'].includes(userRole);
  const date = day === 'custom' && custom ? custom : day === 'today' ? iso(new Date()) : iso(new Date(Date.now() + 86400000));

  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    setLoading(true);
    apiCall(`/api/admin/reception/guest-memory?date_str=${date}`)
      .then((d) => { if (id === reqId.current) setData(d); })
      .catch(() => { if (id === reqId.current) setData(null); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const runBatch = async () => {
    setBatching(true); setBatchMsg('');
    try {
      const r = await apiCall(`/api/admin/reception/guest-memory/ai-hint/batch?date_str=${date}`, { method: 'POST' });
      setBatchMsg(`${r.generated.length} generated · ${r.skipped.length} already fresh${r.failed.length ? ` · ${r.failed.length} failed` : ''}`);
      load();
    } catch (e) { setBatchMsg(e.message); } finally { setBatching(false); }
  };

  const openMail = async () => {
    setMailOpen((o) => !o); setMailMsg('');
    if (!settings) { try { const s = await apiCall('/api/admin/reception/guest-memory/settings'); setSettings(s); setEmail((cur) => cur || s.brief_email || ''); } catch (e) { setMailMsg(e.message); } }
  };
  const saveMail = async (enabled) => {
    setMailBusy(true); setMailMsg('');
    try {
      const r = await apiCall('/api/admin/reception/guest-memory/settings', { method: 'PUT', body: JSON.stringify({ brief_email: email, brief_email_enabled: enabled ?? settings?.brief_email_enabled ?? true }) });
      setSettings((s) => ({ ...s, brief_email: r.brief_email, brief_email_enabled: r.brief_email_enabled })); setMailMsg('Saved');
    } catch (e) { setMailMsg(e.message); } finally { setMailBusy(false); }
  };
  const sendNow = async () => {
    setMailBusy(true); setMailMsg('');
    try {
      const r = await apiCall(`/api/admin/reception/guest-memory/brief/send?date_str=${date}`, { method: 'POST' });
      setMailMsg(r.ok ? `Sent to ${r.recipient}` : `Not sent: ${r.skipped_reason || r.error}`);
    } catch (e) { setMailMsg(e.message); } finally { setMailBusy(false); }
  };

  const rows = (data?.arrivals || []).map((g) => <GuestRow key={g.booking_id} g={g} date={date} canEdit={canEdit} onSaved={load} />);
  const tabs = ['today', 'tomorrow'].map((d) => (
    <button key={d} type="button" onClick={() => setDay(d)} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${day === d ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid={`guest-memory-tab-${d}`}>
      {d === 'today' ? 'Today' : 'Tomorrow'}
    </button>
  ));

  return (
    <div className={`bg-white rounded-xl border border-emerald-200 shadow-sm p-3 sm:p-4 flex flex-col min-h-0 ${compact ? 'max-h-60' : 'max-h-[34rem]'}`} data-testid="guest-memory-card">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <h3 className="font-bold text-slate-900 flex items-center gap-2 text-sm"><Brain className="w-4 h-4 text-emerald-600" /> Guest Memory
          <span className="text-[11px] font-normal text-slate-500">· {date}</span>
        </h3>
        <div className="flex items-center gap-1">
          <GuestSearchBox onPick={setProfileName} />
          {tabs}
          <input type="date" value={custom} onChange={(e) => { setCustom(e.target.value); setDay('custom'); }}
            className={`text-xs border rounded-full px-2 py-0.5 ${day === 'custom' ? 'border-emerald-600 text-emerald-700' : 'text-slate-500'}`} title="Any date" data-testid="guest-memory-date-input" />
          <button type="button" onClick={load} className="p-1 text-slate-500 hover:text-slate-800" title="Refresh" data-testid="guest-memory-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {data && (
        <p className="text-xs text-slate-600 mb-2 flex items-center gap-2" data-testid="guest-memory-counts">
          <span><b>{data.counts.arrivals}</b> arrivals · <b className="text-emerald-700">{data.counts.returning}</b> returning · {data.counts.first_visit} first visit</span>
          {compact && data.counts.arrivals > 0 && (
            <button type="button" onClick={() => setOpen((o) => !o)} className="text-emerald-700 font-semibold hover:underline" data-testid="guest-memory-toggle">
              {open ? 'Hide' : 'Show guests'}
            </button>
          )}
          <span className="flex-1" />
          {canEdit && data.counts.returning > 0 && (
            <button type="button" onClick={runBatch} disabled={batching} className="inline-flex items-center gap-1 text-violet-700 font-semibold hover:underline disabled:opacity-60" title="Generate AI welcome hints for every returning guest (also runs automatically at 18:00)" data-testid="guest-memory-batch-hints">
              {batching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} All hints
            </button>
          )}
          <button type="button" onClick={() => downloadPdf(`/api/admin/reception/guest-memory/brief.pdf?date_str=${date}`, `arrivals-brief-${date}.pdf`)} className="inline-flex items-center gap-1 text-slate-700 font-semibold hover:underline" title="Print one-page arrivals brief for the shift handover" data-testid="guest-memory-print-brief">
            <Printer className="w-3 h-3" /> Print brief
          </button>
          {canManage && (
            <button type="button" onClick={() => downloadPdf('/api/admin/reception/guest-memory/export.csv?min_stays=1', `guest-memory-${iso(new Date())}.csv`)} className="inline-flex items-center gap-1 text-slate-700 font-semibold hover:underline" title="Download every known guest with stays, spend, preferences and contact details (CSV for Excel / marketing)" data-testid="guest-memory-export-csv">
              <Download className="w-3 h-3" /> Export CSV
            </button>
          )}
          {canEdit && (
            <button type="button" onClick={openMail} className="inline-flex items-center gap-1 text-slate-700 font-semibold hover:underline" title="Email the brief every evening after the hints run" data-testid="guest-memory-mail-toggle">
              <Mail className="w-3 h-3" /> Auto-email
            </button>
          )}
          {batchMsg && <span className="text-[11px] text-violet-700" data-testid="guest-memory-batch-msg">{batchMsg}</span>}
        </p>
      )}
      {mailOpen && (
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs bg-slate-50 border rounded-lg px-2.5 py-2" data-testid="guest-memory-mail-panel">
          <span className="text-slate-600">Every evening at 18:00 the brief PDF is emailed to</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="reception@hotel.com" disabled={!canManage}
            className="border rounded px-2 py-0.5 w-56" data-testid="guest-memory-mail-input" />
          {canManage && (
            <>
              <button type="button" onClick={() => saveMail()} disabled={mailBusy} className="font-bold bg-slate-800 text-white rounded px-2 py-0.5 disabled:opacity-60" data-testid="guest-memory-mail-save">Save</button>
              <label className="inline-flex items-center gap-1 text-slate-600">
                <input type="checkbox" checked={settings?.brief_email_enabled ?? true} onChange={(e) => saveMail(e.target.checked)} data-testid="guest-memory-mail-enabled" /> enabled
              </label>
            </>
          )}
          <button type="button" onClick={sendNow} disabled={mailBusy} className="inline-flex items-center gap-1 font-bold text-emerald-700 hover:underline disabled:opacity-60" data-testid="guest-memory-mail-send-now">
            {mailBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Send now
          </button>
          {settings && !settings.resend_configured && <span className="text-amber-700" data-testid="guest-memory-mail-warning">E-mail delivery not configured (Gmail SMTP / Resend) — emails are logged, not delivered.</span>}
          {settings?.last_email && <span className="text-slate-500">Last: {settings.last_email.ok ? 'sent' : (settings.last_email.skipped_reason || 'failed')} · {String(settings.last_email.sent_at).slice(0, 16).replace('T', ' ')}</span>}
          {mailMsg && <span className="text-slate-800 font-semibold" data-testid="guest-memory-mail-msg">{mailMsg}</span>}
        </div>
      )}
      {open && (
      <div className="overflow-y-auto min-h-0 flex-1">
        {!loading && data && rows.length === 0 && <p className="text-xs text-slate-400" data-testid="guest-memory-empty">No arrivals on {date}</p>}
        {!loading && !data && <p className="text-xs text-red-500">Could not load guest memory</p>}
        {rows.length > 0 && <ul className="space-y-2">{rows}</ul>}
      </div>
      )}
      {profileName && <GuestProfileModal name={profileName} userRole={userRole} onClose={() => setProfileName('')} />}
    </div>
  );
};

export default GuestMemoryCard;
